import { useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthPageLayout } from "./auth-page-layout";
import { AuthLoadingState } from "./auth-loading-state";
import { AuthTermsNotice } from "./auth-terms-notice";
import { GoogleSignInButton } from "./google-sign-in-button";
import { useAuth, getAuthErrorMessage } from "@/hooks/use-auth";
import { resendVerificationEmail } from "@/api/auth";
import {
  isDesktopAuthorizeUrl,
  navigateToExternalNext,
  resolveSafeNext,
  withNext,
} from "./next-redirect";
import {
  desktopAvailabilityNotice,
  desktopOsName,
  getDesktopDownloadUrl,
  getDesktopInstallerUrl,
  hasDesktopBuild,
} from "@/lib/desktop-download";
import { trackDesktopDownloadStarted } from "@/lib/posthog";
import {
  Button,
  FormError,
  FormField,
  Icons,
  PasswordInput,
  TextInput,
} from "@/components/ui";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";
import { commonMessages } from "@/i18n/messages/common";

export function LoginPage() {
  const t = useT(authMessages);
  const tc = useT(commonMessages);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get("next");
  const nextUrl = resolveSafeNext(rawNext);
  const registerPath = withNext("/auth/register", nextUrl);
  const showPlatformNotice = !hasDesktopBuild();
  const osName = desktopOsName() ?? "Mac";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompletingSignIn, setIsCompletingSignIn] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [emailPanelOpen, setEmailPanelOpen] = useState(false);
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent">(
    "idle",
  );
  const [choicePanelHeight, setChoicePanelHeight] = useState<number | null>(
    null,
  );
  const [emailPanelHeight, setEmailPanelHeight] = useState<number | null>(null);

  // Both panels are absolutely positioned inside the stage (so the card rides
  // the animated min-height), which means the stage can't grow with content on
  // its own. Measure each panel so platform copy, validation errors, translated
  // text, and narrow-screen wrapping can never be clipped.
  const observeChoicePanel = useCallback((node: HTMLDivElement | null) => {
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setChoicePanelHeight(node.offsetHeight);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const observeEmailPanel = useCallback((node: HTMLDivElement | null) => {
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setEmailPanelHeight(node.offsetHeight);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  function openEmailPanel() {
    setEmailPanelOpen(true);
  }

  function closeEmailPanel() {
    setEmailPanelOpen(false);
    setError("");
    setEmailNotVerified(false);
    setResendStatus("idle");
  }

  async function handleResend() {
    if (!email || resendStatus === "sending") return;
    setResendStatus("sending");
    try {
      await resendVerificationEmail(email);
      setResendStatus("sent");
    } catch {
      setResendStatus("idle");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    setIsCompletingSignIn(true);

    try {
      await login(email, password);
      if (nextUrl) {
        // The desktop authorize `next` (and any other cross-origin backend
        // `next`) 302s server-side — for the desktop case straight to
        // `http://127.0.0.1:<port>/cb`, a scheme+host a `fetch` cannot
        // follow. The browser itself must perform the request via a
        // top-level navigation so cookies travel and it follows the
        // redirect chain.
        if (
          isDesktopAuthorizeUrl(nextUrl) ||
          new URL(nextUrl).origin !== window.location.origin
        ) {
          navigateToExternalNext(nextUrl);
          return;
        }
        navigate(new URL(nextUrl).pathname + new URL(nextUrl).search, {
          replace: true,
        });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      const msg = getAuthErrorMessage(err);
      setError(msg);
      setEmailNotVerified(msg.toLowerCase().includes("not verified"));
      setEmailPanelOpen(true);
      setResendStatus("idle");
      setIsCompletingSignIn(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isCompletingSignIn) {
    return <AuthLoadingState label={t("signingInStatus")} />;
  }

  return (
    <AuthPageLayout landingLink title={t("welcomeBack")}>
      <div
        className="auth-login-stage"
        data-panel={emailPanelOpen ? "email" : "choice"}
        style={
          emailPanelOpen
            ? emailPanelHeight
              ? { minHeight: emailPanelHeight }
              : undefined
            : choicePanelHeight
              ? { minHeight: choicePanelHeight }
              : undefined
        }
      >
        <div
          className="auth-choice-shell"
          data-open={emailPanelOpen ? "false" : "true"}
          aria-hidden={emailPanelOpen}
          data-testid="login-choice-layer"
          inert={emailPanelOpen || undefined}
        >
          <div className="auth-choice-panel" ref={observeChoicePanel}>
            <div className="auth-login-options">
              <button
                type="button"
                className="auth-sign-choice"
                aria-expanded={emailPanelOpen}
                aria-controls="email-sign-in-panel"
                onClick={openEmailPanel}
                data-testid="login-email-option"
                tabIndex={emailPanelOpen ? -1 : undefined}
              >
                <span
                  className="auth-button-icon auth-email-icon"
                  aria-hidden="true"
                >
                  <Icons.Mail />
                </span>
                <span className="auth-button-label">
                  {t("signInWithEmail")}
                </span>
              </button>

              <GoogleSignInButton
                label={t("signInWithGmail")}
                next={nextUrl}
                data-testid="login-google-button"
                tabIndex={emailPanelOpen ? -1 : undefined}
              />
            </div>

            <div className="auth-choice-footer">
              <AuthTermsNotice
                action="continue"
                tabIndex={emailPanelOpen ? -1 : undefined}
              />
              {showPlatformNotice ? (
                <div
                  className="flex max-w-[330px] flex-col items-center gap-1.5"
                  data-testid="login-platform-notice"
                >
                  <p className="text-appkit-caption leading-[1.45] text-[color:var(--auth-ink-secondary)]">
                    {desktopAvailabilityNotice()}
                  </p>
                  <a
                    href={getDesktopDownloadUrl()}
                    className="auth-small-link"
                    data-testid="login-newsletter-link"
                    tabIndex={emailPanelOpen ? -1 : undefined}
                  >
                    <Icons.Mail className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("newsletterForUpdates")}
                  </a>
                </div>
              ) : (
                <a
                  href={getDesktopInstallerUrl()}
                  onClick={() =>
                    trackDesktopDownloadStarted("login_page", osName)
                  }
                  className="auth-small-link"
                  data-testid="login-download-link"
                  tabIndex={emailPanelOpen ? -1 : undefined}
                >
                  <Icons.Download className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("downloadOsApp", { os: osName })}
                </a>
              )}
              <Link
                to={registerPath}
                viewTransition
                className="auth-small-link auth-switch-link"
                data-testid="register-link"
                aria-label={t("signUp")}
                tabIndex={emailPanelOpen ? -1 : undefined}
              >
                {t("signUp")}
              </Link>
            </div>
          </div>
        </div>

        <div
          className="auth-email-panel-shell"
          data-open={emailPanelOpen ? "true" : "false"}
          aria-hidden={!emailPanelOpen}
          data-testid="email-sign-in-panel-shell"
          inert={!emailPanelOpen || undefined}
        >
          <div
            id="email-sign-in-panel"
            className="auth-email-panel"
            data-testid="login-email-panel"
            ref={observeEmailPanel}
          >
            <FormError
              message={error}
              className="mt-0"
              data-testid="login-error"
            />

            <form
              onSubmit={handleSubmit}
              className="auth-login-form flex flex-col"
              data-testid="login-form"
            >
              <FormField label={t("email")} htmlFor="email">
                <TextInput
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  disabled={!emailPanelOpen}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailNotVerified) {
                      setEmailNotVerified(false);
                      setResendStatus("idle");
                    }
                  }}
                  placeholder={t("emailPlaceholder")}
                  className="auth-liquid-input h-12 rounded-[18px] px-4 text-appkit-subhead"
                  data-testid="login-email"
                />
              </FormField>

              <FormField
                label={t("password")}
                htmlFor="password"
                labelAction={
                  <Link
                    to={withNext("/auth/forgot-password", nextUrl)}
                    className="auth-forgot-link auth-inline-link text-appkit-caption"
                    data-testid="forgot-password-link"
                    tabIndex={emailPanelOpen ? undefined : -1}
                  >
                    {t("forgotShort")}
                  </Link>
                }
              >
                <PasswordInput
                  id="password"
                  required
                  autoComplete="current-password"
                  disabled={!emailPanelOpen}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("password")}
                  className="auth-liquid-input h-12 rounded-[18px] text-appkit-subhead"
                  data-testid="login-password"
                />
              </FormField>

              <Button
                type="submit"
                variant="secondary"
                size="lg"
                disabled={!emailPanelOpen || isSubmitting}
                className="auth-liquid-primary-button mt-1 w-full font-semibold"
                data-testid="login-submit"
              >
                {isSubmitting ? t("signingIn") : t("signIn")}
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="auth-small-link auth-back-button"
                onClick={closeEmailPanel}
                data-testid="login-email-back"
                tabIndex={emailPanelOpen ? undefined : -1}
                aria-label={t("backToSignInOptions")}
              >
                <Icons.ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                {tc("back")}
              </Button>
            </form>

            {emailNotVerified && (
              <div
                className="auth-liquid-notice rounded-[18px] px-4 py-3 text-appkit-subhead text-secondary"
                data-testid="resend-verification"
                role="status"
              >
                {resendStatus === "sent" ? (
                  <p>{t("verificationEmailSent")}</p>
                ) : (
                  <p>
                    {t("emailNotVerified")}{" "}
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={!emailPanelOpen || resendStatus === "sending"}
                      className="auth-inline-link disabled:opacity-50"
                      data-testid="resend-verification-button"
                    >
                      {resendStatus === "sending" ? t("sending") : t("resend")}
                    </button>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
}
